import React, { useEffect, useMemo, useState } from "react";
import { Search, StarOff, Plus, X, Pencil } from "lucide-react";
import { motion, AnimatePresence } from "framer-motion";
import { menuItemHasReadAccess } from "../../utils/permissionUtils";
import { useNavigate } from "react-router-dom";
import submenu from "../submenuConfig";
import {
  loadMobileFavoriteIds,
  saveMobileFavoriteIds,
  makeFavoriteId,
  parseFavoriteId,
  MAX_MOBILE_FAVORITES,
} from "../../utils/mobileFavoritesStorage";

const MODULE_LABELS = {
  crm: "CRM",
  hrm: "Nhân sự",
  elearning: "E-Learning",
  finance: "Tài chính",
  reports: "Báo cáo",
  settings: "Cài đặt",
};

function getStoredUserId() {
  try {
    const raw = localStorage.getItem("user");
    if (!raw) return "anonymous";
    const u = JSON.parse(raw);
    return String(u?.id ?? u?._id ?? u?.email ?? "anonymous");
  } catch {
    return "anonymous";
  }
}

function resolveFavoriteEntry(id) {
  const parsed = parseFavoriteId(id);
  if (!parsed) return null;
  const { group, key } = parsed;
  const items = submenu[group];
  if (!items) return null;
  const item = items.find((i) => i.key === key);
  if (!item || item.enabled === false) return null;
  if (!menuItemHasReadAccess(item)) return null;
  return { ...item, group, id };
}

export default function FavoritesScreen() {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState("");
  const [favoriteIds, setFavoriteIds] = useState([]);
  const [pickerOpen, setPickerOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const [pickerSearch, setPickerSearch] = useState("");
  const allowedModules = useMemo(() => {
    return Object.entries(submenu)
      .filter(([_, items]) =>
        items.some((item) => menuItemHasReadAccess(item))
      )
      .map(([key]) => key);
  }, []);

  const pickableFlat = useMemo(() => {
    return Object.entries(submenu)
      .filter(([group]) => allowedModules.includes(group))
      .flatMap(([group, items]) =>
        items
          .filter((item) => item.enabled !== false)
          .filter((item) => menuItemHasReadAccess(item))
          .map((item) => ({
            ...item,
            group,
            id: makeFavoriteId(group, item.key),
          }))
      );
  }, [allowedModules]);

  useEffect(() => {
    const uid = getStoredUserId();
    const loaded = loadMobileFavoriteIds(uid);
    const cleaned = loaded.filter((id) => resolveFavoriteEntry(id));
    if (cleaned.length !== loaded.length) {
      saveMobileFavoriteIds(uid, cleaned);
    }
    setFavoriteIds(cleaned);
  }, []);

  const resolvedFavorites = useMemo(() => {
    return favoriteIds
      .map((id) => {
        const entry = resolveFavoriteEntry(id);
        return entry ? { ...entry, id } : null;
      })
      .filter(Boolean);
  }, [favoriteIds]);

  const filteredDisplay = useMemo(() => {
    const q = searchTerm.toLowerCase();
    return resolvedFavorites.filter((item) =>
      item.label.toLowerCase().includes(q)
    );
  }, [resolvedFavorites, searchTerm]);

  const pickerCandidates = useMemo(() => {
    const set = new Set(favoriteIds);
    const q = pickerSearch.toLowerCase();
    return pickableFlat.filter(
      (item) =>
        !set.has(item.id) && item.label.toLowerCase().includes(q)
    );
  }, [pickableFlat, favoriteIds, pickerSearch]);

  const groupedPicker = useMemo(() => {
    const map = {};
    for (const item of pickerCandidates) {
      if (!map[item.group]) map[item.group] = [];
      map[item.group].push(item);
    }
    return map;
  }, [pickerCandidates]);

  const addFavorite = (id) => {
    setFavoriteIds((prev) => {
      if (prev.length >= MAX_MOBILE_FAVORITES) return prev;
      if (prev.includes(id)) return prev;
      const next = [...prev, id];
      saveMobileFavoriteIds(getStoredUserId(), next);
      return next;
    });
    setPickerSearch("");
  };

  const removeFavorite = (id) => {
    setFavoriteIds((prev) => {
      const next = prev.filter((x) => x !== id);
      saveMobileFavoriteIds(getStoredUserId(), next);
      return next;
    });
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    show: { opacity: 1, transition: { staggerChildren: 0.08 } },
  };

  return (
    <div className="min-h-full bg-[#F8FAFC] pb-6">
      <div className="bg-white px-5 pt-8 pb-6 rounded-b-[1.5rem] shadow-sm border-b border-gray-100">
        <div className="mb-5 flex items-start justify-between gap-3 pl-1">
          <div className="min-w-0 flex-1">
            <h2 className="text-xl font-black text-gray-800 uppercase leading-none tracking-wider">
              Chức năng{" "}
              <span className="text-red-700">yêu thích</span>
            </h2>
            <p className="text-[11px] font-bold text-gray-400 uppercase tracking-[0.2em] mt-2 italic">
              Truy cập nhanh chức năng
              <br />
              cần sử dụng thường xuyên
            </p>
          </div>
          <div className="flex shrink-0 gap-2">
            <button
              type="button"
              onClick={() => {
                setEditMode((e) => !e);
                setPickerOpen(false);
              }}
              className={`flex h-11 w-11 items-center justify-center rounded-2xl border shadow-sm transition active:scale-95 ${
                editMode
                  ? "border-red-300 bg-red-100 text-red-800"
                  : "border-gray-200 bg-white text-gray-600"
              }`}
              title={editMode ? "Xong" : "Sửa danh sách"}
              aria-label={editMode ? "Xong" : "Sửa danh sách"}
            >
              <Pencil className="h-5 w-5" strokeWidth={2.25} />
            </button>
            <button
              type="button"
              onClick={() => {
                setPickerOpen(true);
                setPickerSearch("");
              }}
              disabled={favoriteIds.length >= MAX_MOBILE_FAVORITES}
              className="flex h-11 w-11 items-center justify-center rounded-2xl border border-red-200 bg-red-50 text-red-700 shadow-sm transition hover:border-red-300 hover:bg-red-100 active:scale-95 disabled:opacity-40"
              title="Thêm chức năng"
              aria-label="Thêm chức năng"
            >
              <Plus className="h-5 w-5" strokeWidth={2.5} />
            </button>
          </div>
        </div>

        <p className="text-[10px] font-bold text-gray-400 uppercase tracking-wider mb-3 pl-1">
          {favoriteIds.length}/{MAX_MOBILE_FAVORITES} mục · Lưu trên thiết bị
        </p>

        <div className="relative mt-1">
          <div className="absolute inset-y-0 left-4 flex items-center pointer-events-none text-gray-400">
            <Search size={18} />
          </div>
          <input
            type="text"
            placeholder="Tìm trong yêu thích..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full bg-gray-50 border-none rounded-2xl py-4 pl-12 pr-4 text-sm font-bold focus:ring-2 focus:ring-red-100 transition-all shadow-inner outline-none"
          />
        </div>
      </div>

      <motion.div
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="grid grid-cols-2 gap-4 px-5 pt-4"
      >
        <AnimatePresence mode="popLayout">
          {filteredDisplay.length > 0 ? (
            filteredDisplay.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.9 }}
                transition={{ duration: 0.2 }}
                className="relative"
              >
                {editMode && (
                  <button
                    type="button"
                    onClick={() => removeFavorite(item.id)}
                    className="absolute -right-1 -top-1 z-10 flex h-8 w-8 items-center justify-center rounded-full bg-gray-800 text-white shadow-md active:scale-95"
                    aria-label="Bỏ yêu thích"
                  >
                    <X className="h-4 w-4" strokeWidth={2.5} />
                  </button>
                )}
                <ModuleCard
                  item={item}
                  groupKey={item.group}
                  navigate={navigate}
                  disabled={editMode}
                />
              </motion.div>
            ))
          ) : (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="col-span-2 py-20 flex flex-col items-center justify-center text-center px-10"
            >
              <div className="w-16 h-16 bg-gray-50 rounded-[2rem] flex items-center justify-center mb-4 border-2 border-dashed border-gray-200">
                <StarOff size={24} className="text-gray-300" />
              </div>
              <p className="text-[10px] font-black text-gray-400 uppercase tracking-[0.2em] italic leading-relaxed">
                {searchTerm
                  ? "Không tìm thấy trong yêu thích"
                  : "Chưa có mục nào — nhấn + để thêm"}
              </p>
            </motion.div>
          )}
        </AnimatePresence>
      </motion.div>

      <AnimatePresence>
        {pickerOpen && (
          <motion.div
            className="fixed inset-0 z-[100] flex flex-col justify-end bg-black/40"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setPickerOpen(false)}
          >
            <motion.div
              initial={{ y: "100%" }}
              animate={{ y: 0 }}
              exit={{ y: "100%" }}
              transition={{ type: "spring", damping: 28, stiffness: 320 }}
              className="max-h-[85vh] overflow-hidden rounded-t-[1.5rem] bg-[#F8FAFC] shadow-2xl"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="border-b border-gray-100 bg-white px-5 pt-4 pb-3">
                <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-gray-200" />
                <div className="flex items-center justify-between gap-3">
                  <h3 className="text-sm font-black uppercase tracking-wider text-gray-800">
                    Thêm chức năng
                  </h3>
                  <button
                    type="button"
                    onClick={() => setPickerOpen(false)}
                    className="rounded-xl p-2 text-gray-500 hover:bg-gray-100"
                    aria-label="Đóng"
                  >
                    <X className="h-5 w-5" />
                  </button>
                </div>
                <div className="relative mt-3">
                  <div className="absolute inset-y-0 left-3 flex items-center pointer-events-none text-gray-400">
                    <Search size={16} />
                  </div>
                  <input
                    type="text"
                    placeholder="Tìm chức năng..."
                    value={pickerSearch}
                    onChange={(e) => setPickerSearch(e.target.value)}
                    className="w-full rounded-xl border border-gray-100 bg-gray-50 py-3 pl-10 pr-3 text-sm font-bold outline-none focus:ring-2 focus:ring-red-100"
                    autoFocus
                  />
                </div>
              </div>
              <div className="max-h-[55vh] overflow-y-auto px-3 pb-8 pt-2">
                {pickerCandidates.length === 0 ? (
                  <p className="py-10 text-center text-[11px] font-bold uppercase tracking-wider text-gray-400">
                    {pickerSearch
                      ? "Không có kết quả"
                      : "Đã thêm hết hoặc không còn mục phù hợp"}
                  </p>
                ) : (
                  Object.entries(groupedPicker).map(([group, items]) => (
                    <div key={group} className="mb-4">
                      <p className="px-2 pb-2 text-[10px] font-black uppercase tracking-[0.2em] text-gray-400">
                        {MODULE_LABELS[group] ?? group}
                      </p>
                      <div className="space-y-1">
                        {items.map((item) => (
                          <button
                            key={item.id}
                            type="button"
                            onClick={() => {
                              addFavorite(item.id);
                              setPickerOpen(false);
                            }}
                            className="flex w-full items-center gap-3 rounded-xl bg-white px-3 py-3 text-left shadow-sm border border-gray-100 active:bg-red-50"
                          >
                            <div className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-red-50 text-red-700">
                              <item.icon size={20} />
                            </div>
                            <span className="text-[12px] font-black uppercase tracking-wide text-gray-800">
                              {item.label}
                            </span>
                          </button>
                        ))}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

const ModuleCard = ({ item, groupKey, navigate, disabled }) => (
  <button
    type="button"
    onClick={() => {
      if (!disabled) navigate(`/${groupKey}/${item.key}`);
    }}
    disabled={disabled}
    className="w-full flex flex-col items-center justify-center p-6 rounded-[2rem] bg-white border border-gray-50 shadow-sm transition-all disabled:opacity-90 disabled:active:scale-100 active:bg-red-50 active:scale-95"
  >
    <div className="p-3.5 rounded-2xl bg-red-50 text-red-700 mb-3">
      <item.icon size={28} />
    </div>
    <span className="text-[11px] font-black text-center text-gray-700 uppercase tracking-wider leading-tight">
      {item.label}
    </span>
  </button>
);
